import { Component, DestroyRef, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogResult, PopupDialog } from '@app/Common';
import { CreateTenantTrial } from '@app/Models/create-tenant-trial.model';
import { Tenant } from '@app/Models/tenant';
import { TenantEnvironmentDetails } from '@app/Models/tenant-environment-details.model';
import { TenantService } from '@app/Services';
import { CloneService } from '@app/Tenant/Applications/Airsweb/Services';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCard, MatCardContent, MatCardActions, MatCardFooter } from '@angular/material/card';
import { MatDivider } from '@angular/material/divider';
import { MatButton } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { MatProgressBar } from '@angular/material/progress-bar';
import { NewTenantTrialFormComponent } from '../new-tenant-trial-form/new-tenant-trial-form.component';

@Component({
    selector: 'app-new-tenant-trial',
    templateUrl: './new-tenant-trial.component.html',
    styleUrls: ['./new-tenant-trial.component.scss'],
    imports: [MatCard, MatCardContent, MatDivider, MatCardActions, MatButton, RouterLink, MatCardFooter, MatProgressBar, NewTenantTrialFormComponent, AsyncPipe]
})
export class NewTenantTrialComponent {
    readonly cloneInProgress$: Observable<boolean>;
    readonly cloneFinalised$: Observable<boolean>;
    cloneFromTenantValue: string;
    cloneToTenantValue: string;
    cloneFromTenant: Tenant;
    cloneToTenant: Tenant;
    readonly cloneFromTenant$: Observable<Tenant>;
    cloneToTenant$: Observable<Tenant>;
    cloneToTenantOptions$: Observable<Tenant[]>;
    cloneFromEnvironment$: Observable<TenantEnvironmentDetails>;
    readonly cloneJobId$: Observable<string>;
    cloneStarted$: Subject<boolean> = new Subject<boolean>();

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly tenantService: TenantService,
        private readonly cloneService: CloneService,
        private readonly dialog: MatDialog,
    ) {
        this.cloneInProgress$ = this.cloneService.cloneInProgress$;
        this.cloneFinalised$ = this.cloneService.cloneFinalised$;
        this.cloneFromTenant$ = this.cloneService.sourceTenant$;
        this.cloneJobId$ = this.cloneService.cloneJobId$;
    }

    createTrial(trial: CreateTenantTrial): void {
        this.cloneFromTenant = this.tenantService.tenants
            .find((tenant) => tenant.Value == trial.BestPracticeTenant);

        this.cloneFromEnvironment$ = this.getEnvironmentForTenant(
            this.cloneFromTenant.Value
          );

        const dialogRef = this.createConfirmationPopup(trial);
        dialogRef.afterClosed()
            .pipe(
                filter((result) => !!result && result.Result),
                switchMap(() => this.cloneFromEnvironment$),
                switchMap((cloneFromEnvironment) => {
                    this.cloneStarted$.next(true);
                    return this.cloneService.cloneFreeTrial(this.cloneFromTenant, cloneFromEnvironment.EnvironmentId, trial);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
        .subscribe();
    }

    openLogs
    
    private createConfirmationPopup(trial: CreateTenantTrial): MatDialogRef<PopupDialog, DialogResult> {
        const dialogRef = this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
        dialogRef.componentInstance.DialogTitle = 'Free Trial';
        dialogRef.componentInstance.DialogDescription =
            `Are you sure you want to create a new Free Trial from tenant <b>${this.cloneFromTenant.Value}</b>?`;
        dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
        dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

        return dialogRef;
    }

    private getEnvironmentForTenant(tenantName: string): Observable<TenantEnvironmentDetails> {
        return this.tenantService
          .getEnvironmentDetailsByTenant(tenantName)
          .pipe(
            filter((environmentDetails: TenantEnvironmentDetails | null) => environmentDetails !== null)
          );
      }
}
